// <s-confetti> fires a burst of brand-colored confetti when a `data-event`
// (default "kit:complete") bubbles up to the document. The burst starts at the
// element named by `data-origin` inside the event target (default
// "[data-kit-savings]", the "you saved" line), shoots outward and upward across
// the whole screen, then falls with gravity. Drawn on a fixed, click-through
// canvas for about 4 seconds, then removed. Skipped for reduced motion.
const COLORS = ['#f39c12', '#f4b400', '#1f5c3a', '#2e7d4f', '#ffffff', '#1a1a1a', '#e74c3c'];
const DURATION = 4500;
const GRAVITY = 0.28;
const DRAG = 0.985;

class SConfetti extends HTMLElement {
  #ac;
  #frame = 0;
  #canvas = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const eventName = this.dataset.event || 'kit:complete';
    document.addEventListener(eventName, (event) => this.burst(this.#origin(event.target)), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stop();
  }

  // Viewport point the confetti bursts from: the origin element's center, or the screen center.
  #origin(target) {
    const selector = this.dataset.origin || '[data-kit-savings]';
    const source = target instanceof Element ? (target.querySelector(selector) || target) : null;
    const rect = source?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.bottom < 0 || rect.top > window.innerHeight) {
      return { x: window.innerWidth / 2, y: window.innerHeight * 0.6 };
    }
    // Keep the launch point on screen (tall origins such as <body> would center off-screen).
    const y = Math.min(Math.max(rect.top + rect.height / 2, window.innerHeight * 0.2), window.innerHeight * 0.85);
    return { x: rect.left + rect.width / 2, y };
  }

  burst(origin = { x: window.innerWidth / 2, y: window.innerHeight * 0.6 }) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#stop();

    // The canvas sits in a manual popover so it renders in the top layer, above an
    // open modal such as the cart drawer; without popover support it is a fixed overlay.
    const layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.cssText = 'position:fixed;inset:0;width:100vw;height:100dvh;max-width:none;max-height:none;margin:0;padding:0;border:0;background:transparent;overflow:visible;pointer-events:none;z-index:2147483000';
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'display:block;width:100%;height:100%';
    layer.append(canvas);
    document.body.append(layer);
    if (typeof layer.showPopover === 'function') {
      layer.popover = 'manual';
      try {
        layer.showPopover();
      } catch {
        // Already shown or unsupported: the fixed overlay still works below modals.
      }
    }
    this.#canvas = layer;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = window.innerWidth;
    const height = window.innerHeight;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);

    // Launch speed scales with the distance to the top of the screen so the strongest pieces reach it.
    const topSpeed = Math.max(12, origin.y * 0.043);
    const count = width < 768 ? 140 : 220;
    const pieces = Array.from({ length: count }, () => {
      // Mostly upward fan (about 150 degrees wide), with a few pieces flying sideways.
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * (Math.random() < 0.8 ? 0.85 : 1.3);
      const speed = topSpeed * (0.35 + Math.random() * 0.65);
      return {
        x: origin.x + (Math.random() - 0.5) * 30,
        y: origin.y + (Math.random() - 0.5) * 10,
        w: 6 + Math.random() * 6,
        h: 8 + Math.random() * 8,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        angle: Math.random() * Math.PI * 2,
        spin: -0.25 + Math.random() * 0.5,
        sway: Math.random() * Math.PI * 2,
        round: Math.random() < 0.3,
      };
    });

    const start = performance.now();
    const draw = (now) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, width, height);
      // Fade out over the last second.
      ctx.globalAlpha = Math.max(0, Math.min(1, (DURATION - elapsed) / 1000));
      for (const p of pieces) {
        p.vx *= DRAG;
        p.vy = p.vy * DRAG + GRAVITY;
        // Once falling, cap the speed and add a gentle sway so pieces flutter down.
        if (p.vy > 4.5) p.vy = 4.5;
        p.sway += 0.06;
        p.x += p.vx + (p.vy > 0 ? Math.sin(p.sway) * 0.9 : 0);
        p.y += p.vy;
        p.angle += p.spin;
        if (p.y > height + 30) continue;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.color;
        const outlined = p.color === '#ffffff';
        if (outlined) {
          ctx.strokeStyle = 'rgba(0,0,0,0.12)';
          ctx.lineWidth = 1;
        }
        if (p.round) {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
          if (outlined) ctx.stroke();
        } else {
          // Squash with the spin so pieces look like they flutter.
          const flutter = Math.abs(Math.cos(p.angle * 1.5));
          ctx.fillRect(-p.w / 2, (-p.h / 2) * flutter, p.w, p.h * flutter);
          if (outlined) ctx.strokeRect(-p.w / 2, (-p.h / 2) * flutter, p.w, p.h * flutter);
        }
        ctx.restore();
      }
      if (elapsed < DURATION) {
        this.#frame = requestAnimationFrame(draw);
      } else {
        this.#stop();
      }
    };
    this.#frame = requestAnimationFrame(draw);
  }

  #stop() {
    cancelAnimationFrame(this.#frame);
    this.#canvas?.remove();
    this.#canvas = null;
  }
}

if (!customElements.get('s-confetti')) customElements.define('s-confetti', SConfetti);
